// pages/overlay/ScreenShare.jsx
// Full-frame overlay for screen-share scenes. Brand pink frame with smooth
// rounded inner corners, soft inner glow, artist cam PiP, and a warm bottom
// bar with queue count + social handles in display font.

import useOrientation from "./../../hooks/useOrientation";
import useOverlayPalette from "./../../hooks/useOverlayPalette";
import useStreamConfig from "./../../hooks/useStreamConfig";
import useCurrentShiftOrders from "./../../hooks/useCurrentShiftOrders";

const DISPLAY = "var(--jop-display)";
const FRAME = 28;
const BOTTOM_H = 62;
const RADIUS = 20;
const PAD = 20;

export default function ScreenShare() {
  useOverlayPalette();
  const orientation = useOrientation();
  const portrait = orientation === "portrait";
  // Portrait (1080 wide) gets a smaller PiP — 500px would eat half the frame.
  const CAM_W = portrait ? 380 : 500;
  const CAM_H = Math.round(CAM_W * 9 / 16);
  const config = useStreamConfig();
  const { allActive } = useCurrentShiftOrders();
  const social = config.featuredSocials?.twitch
    || config.featuredSocials?.instagram
    || config.featuredSocials?.web
    || "thejoyofpancakes.com";
  const queueCount = allActive.length;

  return (
    <div style={{
      width: "100vw",
      height: "100vh",
      background: "transparent",
      fontFamily: "system-ui, -apple-system, sans-serif",
      userSelect: "none",
      overflow: "hidden",
      position: "relative",
      pointerEvents: "none",
    }}>
      {/* Transparent cutout with box-shadow frame */}
      <div style={{
        position: "absolute",
        top: `${FRAME}px`,
        left: `${FRAME}px`,
        right: `${FRAME}px`,
        bottom: `${BOTTOM_H}px`,
        borderRadius: `${RADIUS}px`,
        boxShadow: `
          0 0 0 ${Math.max(FRAME, BOTTOM_H) + 100}px var(--jop-pink),
          inset 0 0 30px rgba(var(--jop-pink-rgb),0.15)
        `,
        animation: "jopFrameIn 0.6s ease-out",
      }} />

      {/* Bottom bar — warm pink with display font */}
      <div style={{
        position: "absolute",
        bottom: 0, left: 0, right: 0,
        height: `${BOTTOM_H}px`,
        background: "var(--jop-pink)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 2rem",
        zIndex: 2,
      }}>
        <img
          src="/jop_logo_wide.png"
          alt="JoP"
          style={{ height: "120px", objectFit: "contain", flexShrink: 0, marginTop: "-50px", filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.4))" }}
        />

        {/* Portrait bar is 1080px — drop the center wordmark, the social
            handle on the right already covers the URL */}
        {!portrait && (
          <span style={{
            color: "white", fontWeight: 700, fontFamily: DISPLAY,
            fontSize: "1.2rem", letterSpacing: "0.03em",
          }}>
            thejoyofpancakes.com
          </span>
        )}

        {/* Queue count */}
        {queueCount > 0 && (
          <span style={{
            color: "var(--jop-cream)", fontWeight: 700, fontFamily: DISPLAY,
            fontSize: "1rem",
            background: "rgba(0,0,0,0.2)",
            borderRadius: "999px",
            padding: "0.2rem 0.8rem",
          }}>
            🎟️ {queueCount} waiting
          </span>
        )}

        <span style={{
          color: "var(--jop-cream)", fontWeight: 700, fontFamily: DISPLAY,
          fontSize: "1.1rem", flexShrink: 0,
        }}>
          {social}
        </span>
      </div>

      {/* Artist cam frame — MYC gradient, matches Main overlay exactly */}
      <div style={{
        position: "absolute",
        right: `${PAD}px`,
        bottom: `${BOTTOM_H + PAD}px`,
        width: `${CAM_W}px`,
        height: `${CAM_H}px`,
        borderRadius: "14px",
        overflow: "hidden",
        boxShadow: "0 0 20px rgba(var(--jop-slot-drawing-rgb),0.3), 0 0 40px rgba(var(--jop-slot-done-rgb),0.15)",
        zIndex: 3,
      }}>
        <div style={{
          width: "100%", height: "100%",
          border: "8px solid",
          borderImage: "linear-gradient(135deg, var(--jop-slot-drawing) 0%, var(--jop-slot-cooking) 50%, var(--jop-slot-done) 100%) 1",
          boxSizing: "border-box",
          background: "transparent",
        }} />
        <div style={{
          position: "absolute", top: "10px", left: "12px",
          background: "rgba(var(--jop-ink-rgb),0.85)", border: "1px solid rgba(var(--jop-gold-rgb),0.35)",
          borderRadius: "8px", padding: "3px 10px",
          color: "var(--jop-gold)", fontSize: "0.85rem", fontWeight: 900,
          textTransform: "uppercase", letterSpacing: "0.1em",
          fontFamily: DISPLAY,
        }}>🎙 Artist Cam</div>
      </div>

      <style>{`
        @keyframes jopFrameIn {
          0%   { opacity: 0; }
          100% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
